import React, { useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'

import { Text } from '../../components/topography'
import Icon, { ArrowIcon } from '../../components/icon/Icon'
import { translate } from '../../utils/translation'
import { palette, fonts, mediaQueries, shadows } from '../../styles/variables'
import freeTrialBadge from '../../styles/assets/pricing/eclipse.png'
import lifetimeMobileArrow from '../../styles/assets/pricing/arrow.svg'
import neilPatel from '../../styles/assets/pricing/neil_patel.png'
import bgBestValue from '../../styles/assets/pricing/bg_best_value.svg'
import specialOffer from '../../styles/assets/pricing/special_offer_bg.svg'
import sliderLeftIcon from '../../styles/assets/pricing/slider-left.svg'
import sliderRightIcon from '../../styles/assets/pricing/slider-right.svg'
import customPlan from '../../styles/assets/pricing/custom_plan.svg'
import { Mobile } from '../../utils/media'
import { OrangeButton, OutlineButton } from '../../components/buttons/Buttons'
import GoogleSignIn from '../../components/buttons/GoogleSignIn'
import InquiryCustomPlanModal from '../../components/modal/InquiryCustomPlanModal'
import ConfirmationModal from '../../components/modal/ConfirmationModal'
import { Mixpanel } from '../../utils/mixpanel'
import { getLoggedUser } from '../../utils/userAuth'
import { isPaidUser } from '../../utils/freeVersionBlock'

export const Container = styled.div({
  height: '100vh',
  width: '100%',
  overflowY: 'scroll',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  [mediaQueries.mobile]: {
    height: 'calc(100vh - 69px)',
    background: palette.white,
  },
})

export const Content = styled.div((props) => ({
  height: 'fit-content',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px 5vw',
  justifyContent: 'flex-start',
  background: props.background || palette.lightGray20,
  [mediaQueries.mobile]: {
    margin: '0px',
    padding: props.mobilePadding || props.padding || '20px 5vw',
    background: props.mobileBackground || props.background || palette.white,
  },
}))

export const NeilTipPanel = styled.div(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10,
  marginTop: '30px',
  [mediaQueries.mobile]: {
    flexDirection: 'column',
    marginTop: '15px',
  },
}))

export const NeilTip = styled.div((props) => ({
  flex: 1,
  color: palette.darkGray,
  fontFamily: fonts.primaryLight,
  whiteSpace: 'pre-wrap',
  fontSize: '20px',
  padding: props.padding || '0',
  [mediaQueries.mobile]: {
    padding: props.mobilePadding || props.padding || '0',
  },
}))

export const Price = styled.div((props) => ({
  display: 'flex',
  flexDirection: props.planInterval === 'lifetime' ? 'column' : 'row',
  alignItems: 'center',
  color: props.color ? props.color : palette.darkGray,
  fontSize: props.fontSize ? props.fontSize : '24px',
  fontFamily: props.font ? props.font : fonts.primaryLight,
  margin: props.margin || '0',
  lineHeight: props.planInterval === 'lifetime' ? '24px' : '36px',
  '& b': {
    color: props.color ? props.color : palette.darkGray,
    fontFamily: fonts.primaryMedium,
    fontWeight: 'normal',
    marginRight: 5,
  },
  '& small': {
    color: palette.darkGray50,
    fontSize: '14px',
    lineHeight: '18px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginLeft: 5,
    textAlign: 'left',
  },
  '& span': {
    fontFamily: fonts.primaryLight,
    fontSize: '18px',
    lineHeight: '36px',
    display: 'flex',
    marginLeft: 5,
  },
}))

export const TitleLi = styled.li((props) => ({
  listStyleType: 'none',
  color: palette.neutral01,
  fontFamily: fonts.primaryMedium,
  fontSize: '16px',
  lineHeight: '26px',
  minHeight: '26px',
  cursor: props.cursor || 'auto',
  visibility: props.hidden ? 'hidden' : 'visible',
  [mediaQueries.mobile]: {
    fontSize: '15px',
  },
  '& ul': {
    display: props.expanded ? 'block' : 'none',
  },
}))

export const SecondaryUl = styled.ul({
  padding: '0',
  margin: '16px 0 0 0',
  [mediaQueries.mobile]: {
    padding: 0,
    margin: 0,
  },
})

export const ItemLi = styled.li((props) => ({
  fontFamily: fonts.primary,
  alignItems: 'center',
  justifyContent: props.collapse ? 'center' : 'inherit',
  listStyleType: 'none',
  textDecoration: 'none',
  fontSize: '16px',
  lineHeight: '20px',
  height: '44px',
  fontWeight: 'normal',
  color: palette.darkGray70,
  '&:last-child': {
    height: 'auto',
  },
  [mediaQueries.mobile]: {
    fontSize: '14px',
    position: 'relative',
    height: '48px !important',
    '&:first-child .label': {
      borderTop: `1px solid ${palette.lightGray50}`,
    },
    '&:last-child .label': {
      borderBottom: `1px solid ${palette.lightGray50}`,
    },
  },
}))

export const ItemLiText = styled.div((props) => {
  return {
    color: props.color || palette.darkGray70,
    fontFamily: props.bold ? fonts.primaryMedium : 'inherit',
    display: 'flex',
    alignItems: 'center',
    [mediaQueries.mobile]: {
      fontFamily: fonts.primaryLight,
      height: '100%',
    },
  }
})

export const PlanBadge = styled.div((props) => {
  let fontSize = '22px'
  if (props.medium) fontSize = '11px'
  else if (props.small) fontSize = '13px'
  if (props.type === 'left') {
    return {
      background: `url(${freeTrialBadge}) no-repeat`,
      color: palette.white,
      whiteSpace: 'pre-wrap',
      width: '142px',
      height: '142px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      fontSize: props.small || props.medium ? '11px' : '14px',
      lineHeight: '11px',
      transform: 'rotate(-20deg)',
      padding: '0 0 8px 17px',
      '& b': {
        fontFamily: fonts.primaryMedium,
        fontSize,
        fontWeight: 300,
        lineHeight: '30px',
      },
      ...(props.position === 'absolute'
        ? {
            position: 'absolute',
            right: '-150px',
            top: '-55px',
          }
        : {}),
    }
  }
  return {
    background: `url(${freeTrialBadge}) no-repeat`,
    color: palette.white,
    whiteSpace: 'pre-wrap',
    width: '142px',
    height: '142px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    fontSize: props.small || props.medium ? '11px' : '14px',
    lineHeight: '22px',
    position: 'absolute',
    top: '-38px',
    right: '-64px',
    transform: 'rotate(20deg)',
    padding: '0 0 8px 20px',
    '& b': {
      fontFamily: fonts.primaryMedium,
      fontSize,
      fontWeight: 300,
    },
  }
})

export const SignInArrow = styled.div({
  height: '30px',
  display: 'flex',
  alignItems: 'flex-start',
  color: palette.darkGray,
  fontSize: '14px',
  whiteSpace: 'pre-wrap',
})

export const SignInTrial = styled.img({
  marginLeft: 5,
  marginTop: 3,
})

const LeftArrow = styled.div({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  border: '0',
  padding: '0',
  marginLeft: '-10px',
  marginRight: '10px',
  backgroundColor: 'transparent',
  borderLeft: `1px solid ${palette.orange}`,
  borderTop: `1px solid ${palette.orange}`,
  transform: 'translate(5px, -2px) rotate(135deg)',
})

export const Logos = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  maxWidth: '1024px',
  alignItems: 'center',
  justifyContent: 'space-between',
  margin: '0 auto',
  [mediaQueries.mobile]: {
    padding: '15px 0',
  },
})

export const Logo = styled.img({
  maxWidth: '100%',
  height: 'auto',
  margin: '0 3%',
  [mediaQueries.mobile]: {
    margin: '20px 10px',
    maxWidth: '80%',
  },
})

export const Table = styled.table({
  width: '100%',
  // maxWidth: '1000px',
  backgroundColor: palette.transparent,
  borderSpacing: 0,
  [mediaQueries.mobile]: {
    width: '100%',
  },
  tableLayout: 'fixed',
})

export const Row = styled.tr(() => ({
  width: '100%',
  '&:first-child': {
    '& td:nth-child(2)': {
      boxShadow: '-5px -5px 10px 5px #eeeeee66',
    },
    '& td:nth-child(1n+3)': {
      boxShadow: '7px -5px 10px 5px #eeeeee66',
    },
    '& td:last-child': {
      boxShadow: '7px -5px 10px 5px #eeeeee66',
    },
  },
  '&:nth-child(1n+2)': {
    '& td:nth-child(2)': {
      boxShadow: '-5px 10px 10px 5px #eeeeee66',
    },
    '& td:nth-child(3)': {
      // boxShadow: '5px 12px 22px -15px #eeeeee66',
    },
    '& td:last-child': {
      boxShadow: '5px 10px 10px 5px #eeeeee66',
    },
  },
  '&:last-child': {
    '& td:nth-child(2)': {
      boxShadow: '-5px 10px 10px 5px #eeeeee66',
    },
    '& td:nth-child(1n+3)': {
      boxShadow: '10px 10px 10px 5px #eeeeee66',
    },
    '& td:last-child': {
      boxShadow: '5px 10px 10px 5px #eeeeee66',
    },
  },
  [mediaQueries.mobile]: {
    '& td': {
      boxShadow: 'none !important',
    },
    width: '100%',
  },
}))

export const Cell = styled.td((props) => ({
  width: '100%',
  padding: props.noPadding ? '0' : props.padding || '24px 10px',
  boxSizing: 'border-box',
  borderBottom: props.noBorderBottom
    ? 'none'
    : `1px solid ${palette.neutral05}`,
  borderTop: 'none',
  textAlign: 'center',
  verticalAlign: 'top',
  '&:first-child': {
    width: '25%',
    maxWidth: '600px',
    textAlign: 'left',
    paddingLeft: 0,
    borderLeft: 'none',
  },
  '&:nth-child(1n+2)': {
    width: '25%',
    maxWidth: '500px',
    borderRight: props.noBorder ? 'none' : `1px solid ${palette.neutral05}`,
    background: palette.white,
  },
  '&:last-child': {
    borderRight: 'none',
  },
  [mediaQueries.mobile]: {
    padding: props.noPadding ? '0' : '15px',
    '&:first-child': {
      textAlign: 'center',
      width: '50%',
      border: 'none',
      borderRight: `1px solid ${palette.neutral05}`,
      background: palette.white,
    },
    '&:last-child': {
      width: '50%',
      border: 'none !important',
      background: palette.white,
    },
  },
}))

export const Column = styled.div((props) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: props.height || '290px',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
}))

export const FaqBox = styled.div({
  background: palette.white,
  maxWidth: '1200px',
  width: '100%',
  padding: '76px 10px',
  [mediaQueries.mobile]: {
    background: palette.white,
    padding: '10px 0 50px 0',
  },
})

export const Line = styled.div((props) => ({
  borderBottom: `1px solid ${palette.orange}`,
  height: '20px',
  width: props.double ? '66px' : '33px',
  margin: '0 auto',
  display: 'block',
}))

export const FaqHeader = styled.div((props) => ({
  fontSize: '18px',
  textAlign: 'center',
  position: 'relative',
  margin: props.margin || '0',
  [mediaQueries.mobile]: {
    margin: props.mobileMargin || props.margin || '0',
  },
}))

const FaqItem = styled.div((props) => ({
  width: '100%',
  fontFamily: fonts.primary,
  fontSize: '20px',
  lineHeight: '1.5em',
  borderTop: `1px solid ${palette.lightGray50}`,
  color: palette.neutral01,
  borderLeft: 'none',
  padding: '22px 0 25px',
  position: 'relative',
  cursor: 'pointer',
  '&:last-child': {
    borderBottom: `1px solid ${palette.lightGray50}`,
  },
  '& span': {
    fontSize: '18px',
    fontFamily: fonts.primary,
    color: palette.darkGray,
    display: props.expanded ? 'block' : 'none',
    width: '94%',
    whiteSpace: 'pre-wrap',
    marginTop: '25px',
  },
  '&::after': {
    display: props.expanded ? 'none' : 'block',
    content: `''`,
    height: '17px',
    width: '1px',
    background: palette.neutral01,
    position: 'absolute',
    right: '11px',
    top: '50%',
    transform: 'translateY(-50%)',
    [mediaQueries.mobile]: {
      right: '30px',
      height: '20px',
    },
  },
  '&::before': {
    content: `''`,
    height: '17px',
    width: '1px',
    background: palette.neutral01,
    position: 'absolute',
    right: '11px',
    top: '40%',
    transform: 'rotate(90deg)',
    [mediaQueries.mobile]: {
      right: '29px',
      height: '20px',
    },
  },
  [mediaQueries.mobile]: {
    fontSize: '16px',
    padding: '30px 50px 30px 30px',
    '& span': {
      fontSize: '15px',
    },
  },
}))

export const Faq = ({ faq: { question, answer } }) => {
  const [expanded, setExpanded] = useState(false)
  return (
    <FaqItem expanded={expanded} onClick={() => setExpanded(!expanded)}>
      {translate(question)}
      <span>{translate(answer)}</span>
    </FaqItem>
  )
}

const Card = styled.div(() => ({
  margin: '10px 0',
  fontFamily: fonts.primaryLight,
  fontSize: '24px',
  color: palette.darkGray,
  border: `1px solid ${palette.lightGray50}`,
  padding: '20px',
  position: 'relative',
  cursor: 'pointer',
}))

export const PriceText = styled.div(() => ({
  '& .discount': {
    fontSize: '14px',
    lineHeight: '18px',
    fontFamily: fonts.primaryMedium,
    color: palette.orange,
  },
}))

const CardDetail = styled.div({})

export const TierCard = ({
  card,
  plan,
  mobileFeatures,
  tierButton,
  claimBFOfferButton,
  tierPrices,
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text
          color={palette.orange}
          fontSize="30px"
          fontFamily={fonts.primaryMedium}
        >
          {translate(card.title)}
        </Text>
        {expanded ? (
          <ArrowIcon
            down
            color={palette.orange}
            onClick={() => setExpanded(!expanded)}
          />
        ) : (
          <ArrowIcon
            up
            color={palette.orange}
            onClick={() => setExpanded(!expanded)}
          />
        )}
      </div>
      <Text
        color={palette.darkGray70}
        fontSize="15px"
        lineHeight="1.5em"
        fontFamily={fonts.primaryLight}
        margin="0 0 10px 0"
      >
        {translate(card.description)}
      </Text>
      {tierButton}
      {claimBFOfferButton}
      <PriceText className="PriceText">
        <Text
          color={palette.darkGray}
          boldColor={palette.darkGray}
          fontSize="20px"
          margin="20px 0"
          dangerouslySetInnerHTML={{
            __html: tierPrices[card.tier][plan],
          }}
        />
        {plan === 'yearly' && (
          <Text
            color={palette.orange}
            fontSize="16px"
            margin="20px 0"
            dangerouslySetInnerHTML={{
              __html: translate(
                'yearly_payment',
                tierPrices[card.tier][`${plan}PerYear`],
                '',
              ),
            }}
          />
        )}
      </PriceText>
      {expanded ? (
        <CardDetail>
          {mobileFeatures.map((feature) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                margin: '20px 0 0 0',
              }}
            >
              <Text
                fontFamily={fonts.primaryLight}
                fontSize="16px"
                color={palette.darkGray50}
              >
                {feature.name}
              </Text>
              <Text
                fontSize="16px"
                color={palette.darkGray}
                fontFamily={fonts.primaryMedium}
              >
                {feature[card.tier]}
              </Text>
            </div>
          ))}
        </CardDetail>
      ) : (
        <Text
          color={palette.orange}
          fontSize="16px"
          onClick={() => setExpanded(!expanded)}
          textDecoration="underline"
          fontFamily={fonts.primaryMedium}
        >
          <LeftArrow /> {translate('see_details')}
        </Text>
      )}
    </Card>
  )
}

export const TierCards = styled.div({
  padding: '15px',
  width: '100%',
  [mediaQueries.mobile]: {
    padding: '0 0 15px',
  },
})

const TimerWrapper = styled.div({
  position: 'absolute',
  top: '-28px',
  right: '30px',
  display: 'flex',
  background: palette.darkGray70,
  padding: '0 10px',
  alignItems: 'center',

  [mediaQueries.mobile]: {
    justifyContent: 'center',
    padding: '10px 0 0 0',
    top: '-72px',
    right: 0,
    '& > div': {
      border: 'none',
    },
  },
})

export const SwitchLabelWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flex: 1,
  whiteSpace: 'pre',
})

const LifeTimePlanOfferWrapper = styled.div(() => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  margin: '30px auto 125px',
  position: 'relative',
  width: '100%',
  maxWidth: '1200px',
  boxShadow: '0px 12px 80px rgba(0, 0, 0, 0.05)',
  backgroundColor: palette.lightGray20,
  [mediaQueries.mobile]: {
    margin: '30px auto 15px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingTop: 20,
  },
}))

const TimeLabel = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '24px 20px 16px 20px',
  [mediaQueries.mobile]: {
    padding: '8px',
  },
})

export const Image = styled.img((props) => ({
  width: props.width || 'auto',
  height: props.height || 'auto',
  cursor: props.cursor || 'pointer',
  [mediaQueries.mobile]: {
    width: props.mobileWidth || props.width || 'auto',
    height: props.mobileHeight || props.height || 'auto',
    maxWidth: props.mobileMaxWidth || 'none',
  },
}))

export const FlexContainer = styled.div((props) => ({
  display: 'flex',
  margin: props.margin || '0',
  flexDirection: props.flexDirection || 'row',
  justifyContent: props.justifyContent || 'normal',
  padding: props.padding || 0,
  [mediaQueries.mobile]: {
    flexDirection: props.mobileFlexDirection || props.flexDirection || 'row',
    margin: props.mobileMargin || props.margin || '0',
    alignItems: props.mobileAlignItems || 'normal',
    padding: props.mobilePadding || props.padding || 0,
    zIndex: props.mobileZindex || 'inherit',
  },
  [mediaQueries.tablet]: {
    paddingTop: '70px',
  },
}))

export const LifeTimePlanOffer = ({ languageCode, countDown = 0 }) => {
  const timeLeft = moment.utc(countDown)
  const user = getLoggedUser()
  let hideCounter = false
  if (user?.coupon?.code === 'lifetime-promo-5') {
    if (user?.coupon?.exp) {
      const momentFromTimestamp = moment.unix(user.coupon.exp)
      hideCounter = !momentFromTimestamp.isSameOrBefore(moment())
    } else {
      hideCounter = true
    }
  }
  return (
    <LifeTimePlanOfferWrapper margin="30px auto 125px">
      <Mobile>
        <Image
          src={lifetimeMobileArrow}
          width="60px"
          height="90px"
          style={{
            position: 'absolute',
            left: 'calc(50% + 90px)',
            bottom: '-60px',
            zIndex: 100,
            transform: 'rotate(25deg)',
          }}
        />
      </Mobile>
      <Image
        src={neilPatel}
        width="366px"
        height="auto"
        mobileWidth="50%"
        mobileMaxWidth="200px"
        style={{
          cursor: 'auto',
          position: 'absolute',
          zIndex: '10',
          minWidth: '160px',
          left: '-30px',
        }}
      />
      <FlexContainer
        margin="0 0 0 310px"
        mobileMargin="150px 0 0 0"
        flexDirection="column"
        padding="35px"
        mobilePadding="30px 20px"
        mobileZindex="20"
        style={{
          flex: 1,
          backgroundColor: palette.white,
          position: 'relative',
        }}
      >
        <div>
          <div>
            <Text
              color={palette.darkGray50}
              fontSize={12}
              mobileFontSize={10}
              style={{
                letterSpacing: 1,
              }}
            >
              {translate('special_offer')}
            </Text>
            <Text
              color={palette.orange}
              fontSize={28}
              mobileFontSize={24}
              fontFamily={fonts.primaryMedium}
              margin="10px 0 0 0"
              style={{
                wordBreak: 'break-word',
              }}
            >
              {translate('hate_subscription')}
            </Text>
          </div>
          {countDown > 0 && !hideCounter && !isPaidUser() && (
            <TimerWrapper>
              <SpecialOfferBadge>
                <Text
                  color={palette.white}
                  fontFamily={fonts.primaryMedium}
                  fontSize={
                    languageCode === 'es' ||
                    languageCode === 'nl' ||
                    languageCode === 'pt' ||
                    languageCode === 'de'
                      ? '10px'
                      : '12px'
                  }
                  lineHeight="16px"
                  textAlign="center"
                  style={{ marginBottom: '7px' }}
                >
                  {translate('special_limited_offer')}
                </Text>
              </SpecialOfferBadge>
              <TimeLabel>
                <Text
                  color={palette.white}
                  mobileColor={palette.white}
                  fontSize="24px"
                  fontFamily={fonts.primaryMedium}
                  lineHeight="100%"
                  wordBreak="inherit"
                >
                  {timeLeft.format('HH')}
                </Text>
                <Text
                  color={palette.white}
                  mobileColor={palette.white}
                  fontSize="14px"
                  fontFamily={fonts.primaryLight}
                  lineHeight="100%"
                  wordBreak="inherit"
                  margin="8px 0 0 0"
                >
                  {translate('countdown_hours')}
                </Text>
              </TimeLabel>

              <Text
                color={palette.white}
                fontSize="26px"
                fontFamily={fonts.primaryMedium}
                lineHeight="100%"
                margin="0 0 17px 0"
              >
                :
              </Text>

              <TimeLabel>
                <Text
                  color={palette.white}
                  mobileColor={palette.white}
                  fontSize="24px"
                  fontFamily={fonts.primaryMedium}
                  lineHeight="100%"
                  wordBreak="inherit"
                >
                  {timeLeft.format('mm')}
                </Text>
                <Text
                  color={palette.white}
                  mobileColor={palette.white}
                  fontSize="14px"
                  fontFamily={fonts.primaryLight}
                  lineHeight="100%"
                  wordBreak="inherit"
                  margin="8px 0 0 0"
                >
                  {translate('countdown_minutes')}
                </Text>
              </TimeLabel>

              <Text
                color={palette.white}
                fontSize="26px"
                fontFamily={fonts.primaryMedium}
                lineHeight="100%"
                margin="0 0 17px 0"
              >
                :
              </Text>

              <TimeLabel>
                <Text
                  color={palette.white}
                  mobileColor={palette.white}
                  fontSize="24px"
                  fontFamily={fonts.primaryMedium}
                  lineHeight="100%"
                  wordBreak="inherit"
                >
                  {timeLeft.format('ss')}
                </Text>
                <Text
                  color={palette.white}
                  mobileColor={palette.white}
                  fontSize="14px"
                  fontFamily={fonts.primaryLight}
                  lineHeight="100%"
                  wordBreak="inherit"
                  margin="8px 0 0 0"
                >
                  {translate('countdown_seconds')}
                </Text>
              </TimeLabel>
            </TimerWrapper>
          )}
        </div>
        <Text
          margin="20px 0 0"
          fontSize={18}
          mobileFontSize={14}
          color={palette.darkGray70}
          boldColor={palette.darkGray}
          fontFamily={fonts.primaryLight}
          lineHeight="1.7"
          dangerouslySetInnerHTML={{
            __html: translate('hate_subscription_description'),
          }}
        />
      </FlexContainer>
    </LifeTimePlanOfferWrapper>
  )
}

export const SpecialOfferBadge = styled.div(() => ({
  width: '191px',
  height: '52px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `url(${specialOffer}) no-repeat`,
  backgroundSize: 'cover',
  position: 'absolute',
  top: '-22px',
  left: '50%',
  transform: 'translateX(-50%)',
}))

export const LifePlans = styled.div(() => ({
  display: 'flex',

  alignItems: 'center',
  width: '100%',
  margin: '49px 0 0',
  maxWidth: '1200px',
  position: 'relative',
  paddingBottom: '22px',
}))

export const LifePlanCard = styled.div({
  flex: 1,
  height: '431px',
  background: palette.white,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  '&:nth-child(2)': {
    borderLeft: `1px solid ${palette.gray30}`,
    borderRight: `1px solid ${palette.gray30}`,
  },
})

export const PopularBadge = styled.div(() => ({
  width: '200px',
  height: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `url(${bgBestValue}) no-repeat`,
  backgroundSize: 'cover',
  position: 'absolute',
  top: '-55px',
  left: '50%',
  transform: 'translateX(-50%)',
  [mediaQueries.mobile]: {
    top: '-30px',
    fontSize: '14px',
  },
}))

export const NewLifePlanCard = styled.div(({ isFeatureOn }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  height: 'auto',
  margin: '0 10px',
  position: 'relative',
  border: isFeatureOn ? 'none' : `1px solid ${palette.neutral05}`,
  backgroundColor: palette.white,
  overflow: isFeatureOn ? 'hidden' : 'visible',
  boxShadow: isFeatureOn ? shadows.shadowF : 'none',
  '&:nth-child(2)': {
    overflow: 'hidden',
    border: 'none',
    boxShadow: shadows.shadowF,
  },
}))

const getHeaderBackgroundColor = ({ isFeatureOn, isPopular, theme }) => {
  if (isFeatureOn && theme?.commonPromoBackground)
    return theme.commonPromoBackground
  if (isPopular) return palette.blue
  return palette.accents01
}

export const NewLifePlanCardHeader = styled.div((props) => ({
  width: '100%',
  height: props.isPopular ? '24px' : '16px',
  backgroundColor: getHeaderBackgroundColor(props),
}))

export const NewLifePlanCardContent = styled.div((props) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: props.alignItems || 'inherit',
  justifyContent: 'flex-start',
  padding: props.padding || 0,
  borderTop: props.noBorder ? 'none' : `1px solid ${palette.neutral06}`,
}))

export const PlanBenefitUl = styled.ul({
  padding: 0,
  margin: 0,
})

export const PlanBenefitLi = styled.li({
  listStyleType: 'none',
  textDecoration: 'none',
  fontFamily: fonts.primary,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  marginBottom: '4px',
})

export const FeatureExpandIcon = styled.div((props) => ({
  width: '17px',
  height: '24px',
  cursor: 'pointer',
  position: 'absolute',
  left: '-32px',
  top: '-1px',
  '&::after': {
    display: props.expanded ? 'none' : 'block',
    content: `''`,
    height: '17px',
    width: '1px',
    background: palette.accents01,
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  '&::before': {
    content: `''`,
    height: '1px',
    width: '17px',
    background: props.expanded ? palette.neutral03 : palette.accents01,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  },
}))

export const BestValueBadge = styled.div(({ isFeatureOn, theme }) => {
  return {
    position: 'absolute',
    top: '22px',
    right: '-37px',
    width: '167px',
    height: '40px',
    background: isFeatureOn ? theme.promoBadgeBackground : palette.white,
    transform: 'rotate(43deg)',
    boxShadow: shadows.shadowF,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
})

export const NewAddonTitle = styled.div({
  fontFamily: fonts.primaryMedium,
  fontSize: '16px',
  lineHeight: '26px',
  color: palette.neutral01,
  textAlign: 'left',
  '& b': {
    fontFamily: fonts.primaryMedium,
    color: palette.white,
    fontSize: '12px',
    lineHeight: '12px',
    backgroundColor: palette.accents01,
    borderRadius: '19px',
    padding: '4px 8px',
  },
})

export const NewFeatureItemLi = styled.li(() => ({
  position: 'relative',
  display: 'block',
  padding: '12px 15px 12px 0',
  fontFamily: fonts.primary,
  listStyleType: 'none',
  textDecoration: 'none',
  fontSize: '16px',
  lineHeight: '26px',
  borderBottom: `1px solid ${palette.neutral05}`,
  fontWeight: 'normal',
  color: palette.neutral02,
  '&:last-child': {
    height: 'auto',
    borderBottom: 'none',
  },
  '& > .hidden': {
    visibility: 'hidden',
  },
}))

export const NewFeatureItemText = styled.div(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 15px',
}))

const getMargin = (props) => {
  if (props.originMonth) return '5px'
  return '32px'
}

export const NewPriceText = styled.div((props) => ({
  width: props.noWrap ? 'auto' : '100%',
  fontFamily: fonts.primaryLight,
  fontSize: props.isPopular ? '24px' : '20px',
  lineHeight: props.isPopular ? '32px' : '30px',
  color: palette.neutral03,
  textAlign: 'center',
  margin: `${getMargin(props)} 0 0`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: props.noWrap ? 'none' : 'wrap',
  gap: props.noWrap ? '4px' : '0',
  '& span': {
    fontFamily: fonts.primaryLight,
    marginLeft: '1px',
  },
  '& > b': {
    width: '100%',
    fontFamily: fonts.primaryBook,
    fontSize: props.isPopular ? '48px' : '40px',
    lineHeight: props.isPopular ? '56px' : '48px',
    color: palette.neutral01,
    marginBottom: props.isPopular ? '4px' : '0',
    textAlign: 'center',
  },
  '& .discount': {
    fontSize: '14px',
    lineHeight: '18px',
    fontFamily: fonts.primaryMedium,
    color: palette.orange,
  },
  '& small': {
    fontSize: '14px',
    lineHeight: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginLeft: '5px',
    textAlign: 'left',
    fontFamily: fonts.primary,
    color: palette.neutral03,
    '& > b': {
      fontFamily: fonts.primaryMedium,
      color: palette.neutral01,
    },
  },
}))

export const YearlyPriceText = styled.div((props) => ({
  width: '100%',
  textAlign: props?.textAlign || 'center',
  paddingBottom: '25px',
  color: props?.color || palette.orange,
  fontSize: '14px',
  padding: props?.padding || '10px 10px',
  lineHeight: '26px',
}))

export const SeeMoreLink = styled.div(() => ({
  fontFamily: fonts.primary,
  fontSize: '16px',
  lineHeight: '26px',
  color: palette.neutral03,
  margin: '12px 0 24px',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}))

export const AddonDetailsButton = styled.div({
  fontFamily: fonts.primary,
  textDecoration: 'underline',
  fontSize: '20px',
  lineHeight: '30px',
  color: palette.neutral03,
  cursor: 'pointer',
  marginBottom: '24px',
  '&:hover': {
    textDecoration: 'none',
  },
})

export const PlanButtonWrapper = styled.div((props) => ({
  width: '100%',
  maxWidth: props.maxWidth || '236px',
  '& > span': {
    width: '100%',
    minWidth: 'fit-content',
    height: 'auto',
    padding: props.top ? '15px 0' : '11px 0',
    backgroundColor: props.color,
    color: props.bottom ? palette.accents01 : palette.white,
    border: `1px solid ${palette.transparent}`,
    '&:hover': {
      backgroundColor: props.bottom ? palette.accents01 : palette.white,
      color: props.color,
      '& > .arrow-icon': {
        stroke: props.color,
      },
      border: `1px solid ${props.color}`,
    },
    '& > span': {
      fontSize: '16px',
      lineHeight: '26px',
      ...(props.fontFamily ? { fontFamily: props.fontFamily } : {}),
      ...(props.fontWeight ? { fontWeight: props.fontWeight } : {}),
    },
    '& > .arrow-icon': {
      width: '24px',
      height: '24px',
      stroke: palette.white,
    },
  },
}))

export const SeePlansButton = styled(OrangeButton)({
  fontFamily: fonts.primaryMedium,
  fontSize: '14px',
  lineHeight: '1.5em',
  textTransform: 'capitalize',
  transition: 'background 0.3s ease, color 0.3s ease',
  '&:hover': {
    background: palette.white,
    color: palette.accents01,
  },
})

export const TextLinkButton = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'all',
  cursor: 'pointer',
  '& > p': {
    margin: '0 6px 0 0',
    fontFamily: fonts.primaryMedium,
    lineHeight: '1.5em',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  '& > .arrow-icon': {
    width: '20px',
    height: '20px',
    stroke: palette.neutral02,
  },
  '&:hover': {
    '& > p': {
      textDecoration: 'underline',
    },
  },
})

export const ReviewContainer = styled.div({
  width: '100%',
  maxWidth: '970px',
  margin: '20px 0',
  '.slick-track': {
    display: 'flex',
    gap: '8px',
  },
  '.slick-slide': {
    height: 'inherit !important',
    '> div': {
      height: '100%',
      '> div': {
        height: '100%',
      },
    },
  },
})

export const ReviewCard = styled.div((props) => ({
  width: 'auto',
  maxWidth: '232.25px',
  background: palette.white,
  padding: '16px 10px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: props.alignItems || 'flex-start',
  minHeight: '380px',
  border: '1px solid #E3E7EA',
  cursor: 'grab',
}))

export const ReviewSeparator = styled.div({
  width: '60px',
  background: '#E9E9EA',
  height: '1px',
  margin: '10px 0',
})

export const SliderArrowWrapper = styled.div(() => ({
  width: '40px !important',
  height: '40px !important',
  '&::before': {
    display: 'none',
  },
  '&:hover': {
    opacity: '0.9',
  },
  '&.slick-next': {
    right: '-49px',
  },
  '&.slick-prev': {
    left: '-47px',
  },
}))

export const SliderNextArrow = (props) => {
  const { className, onClick } = props
  return (
    <SliderArrowWrapper className={className} onClick={onClick}>
      <Icon src={sliderRightIcon} size="40px" />
    </SliderArrowWrapper>
  )
}

export const SliderPrevArrow = (props) => {
  const { className, onClick } = props
  return (
    <SliderArrowWrapper className={className} onClick={onClick}>
      <Icon src={sliderLeftIcon} size="40px" />
    </SliderArrowWrapper>
  )
}

const CustomPlanContainer = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  width: '100%',
  maxWidth: '1180px',
  backgroundColor: palette.white,
  padding: '56px 64px',
  margin: '40px 0 49px 0',
  [mediaQueries.mobile]: {
    flexDirection: 'column',
    margin: '20px 0 0 0',
    padding: '20px',
    div: {
      padding: '0',
      margin: '10px 0',
      textAlign: 'center',
    },
  },
  '> div': {
    flexGrow: 1,
    padding: '0 32px',
  },
}))

export const CustomPlanSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  return (
    <CustomPlanContainer>
      {isModalOpen && (
        <InquiryCustomPlanModal
          isOpen
          onClose={() => setIsModalOpen(false)}
          onSubmit={() => {
            Mixpanel.trackCustomPlanFormSubmitSuccess()
            setIsModalOpen(false)
            setIsSubmitted(true)
          }}
        />
      )}
      {isSubmitted && (
        <ConfirmationModal
          isOpen
          title={translate('help_form_success_heading')}
          message={translate('help_form_success_subheading')}
          renderFooter={() => {
            return (
              <OutlineButton
                static
                color={palette.darkGray50}
                onClick={() => setIsSubmitted(false)}
              >
                {translate('close')}
              </OutlineButton>
            )
          }}
        />
      )}
      <Image src={customPlan} cursor="default" width="102px" height="102px" />
      <div>
        <Text
          color={palette.neutral01}
          fontSize="32px"
          fontFamily={fonts.primaryMedium}
          lineHeight="100%"
          fontWeight={500}
        >
          {translate('new_custom_plan_cta_heading')}
        </Text>
        <Text
          color={palette.neutral03}
          fontSize="20px"
          fontFamily={fonts.primary}
          lineHeight="26px"
          margin="20px 0 0 0"
        >
          {translate('new_custom_plan_cta_subheading')}
        </Text>
      </div>
      <PlanButtonWrapper
        top
        color={palette.orange}
        maxWidth="207px"
        fontFamily={fonts.primaryMedium}
        fontWeight={500}
        style={{ padding: 0 }}
      >
        <GoogleSignIn
          useClickEvent
          hideIcon
          label={translate('get_in_touch')}
          minWidth="180px"
          theme="dark"
          mobileStretch
          onClick={() => {
            Mixpanel.trackCustomPlanClick()
            setIsModalOpen(true)
          }}
          hideArrow={false}
          noRedirect
        />
      </PlanButtonWrapper>
    </CustomPlanContainer>
  )
}
